import { useEffect, useRef, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { ChevronDown, Menu, X } from "lucide-react";
import { AnimatePresence, m } from "framer-motion";
import { Logo } from "./Logo";
import { Button } from "@/components/ui/button";
import { services } from "@/data/services";
import { tools } from "@/data/tools";
import { DECISION_SPRINT_LIVE } from "@/lib/flags";


export function SiteNav() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const { location } = useRouterState();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    setToolsOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-midnight/95 backdrop-blur shadow-soft" : "bg-midnight/85 backdrop-blur-sm"
      }`}
      onKeyDown={(e) => {
        if (e.key === "Escape" && mobileOpen) {
          setMobileOpen(false);
          toggleRef.current?.focus();
        }
      }}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4" aria-label="Primary">
        <Link to="/" aria-label="Ajility, go to homepage" className="flex items-center">
          <Logo tone="light" className="h-8 sm:h-9 lg:h-11" linked={false} alt="" priority />
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          <NavLink to="/about">About</NavLink>
          <NavLink to="/how-we-work">How we work</NavLink>

          <div
            className="relative"
            onMouseEnter={() => { setServicesOpen(true); setToolsOpen(false); }}
            onMouseLeave={() => setServicesOpen(false)}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setServicesOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") setServicesOpen(false);
            }}
          >
            <Link
              to="/services"
              className="flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium text-frost/90 transition hover:bg-white/5 hover:text-frost"
              activeProps={{ "aria-current": "page", className: "flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium text-frost bg-white/10" }}
              aria-expanded={servicesOpen}
              aria-haspopup="true"
              aria-controls="services-menu"
              onClick={() => { setServicesOpen(true); setToolsOpen(false); }}
              onFocus={() => { setServicesOpen(true); setToolsOpen(false); }}
            >
              Services
              <ChevronDown className={`h-4 w-4 transition-transform ${servicesOpen ? "rotate-180" : ""}`} aria-hidden="true" />
            </Link>
            <AnimatePresence>
              {servicesOpen && (
                <m.div
                  id="services-menu"
                  initial={{ y: 8 }}
                  animate={{ y: 0 }}
                  exit={{ y: 8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 mt-2 w-80 rounded-2xl border border-white/10 bg-midnight-deep p-4 shadow-elevated"
                >
                  <p id="services-menu-heading" className="mb-3 px-3 text-xs font-semibold uppercase tracking-wider text-frost/75">
                  Fractional COO services and reviews
                  </p>
                  <ul className="space-y-1" aria-labelledby="services-menu-heading">
                    <li>
                      <Link
                        to="/services"
                        className="block rounded-xl px-3 py-2 text-sm text-frost/90 transition hover:bg-white/5 hover:text-frost"
                      >
                        All services
                      </Link>
                    </li>
                    {services.map((s) => (
                      <li key={s.to}>
                        <Link
                          to={s.to}
                          className="block rounded-xl px-3 py-2 text-sm text-frost/90 transition hover:bg-white/5 hover:text-frost"
                        >
                          {s.name}
                        </Link>
                      </li>
                    ))}
                    {DECISION_SPRINT_LIVE ? (
                      <li>
                        <Link
                          to="/decision-sprint"
                          className="block rounded-xl px-3 py-2 text-sm text-frost/90 transition hover:bg-white/5 hover:text-frost"
                        >
                          Decision Sprint
                        </Link>
                      </li>
                    ) : null}
                  </ul>
                </m.div>
              )}
            </AnimatePresence>
          </div>

          <NavLink to="/specialisations">Expertise</NavLink>
          <NavLink to="/packages">Packages</NavLink>

          <NavLink to="/advisers">Advisers</NavLink>
          <div
            className="relative"
            onMouseEnter={() => { setToolsOpen(true); setServicesOpen(false); }}
            onMouseLeave={() => setToolsOpen(false)}
            onBlur={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node)) setToolsOpen(false);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") setToolsOpen(false);
            }}
          >
            <Link
              to="/tools"
              className="flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium text-frost/90 transition hover:bg-white/5 hover:text-frost"
              activeProps={{ "aria-current": "page", className: "flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium text-frost bg-white/10" }}
              aria-expanded={toolsOpen}
              aria-haspopup="true"
              aria-controls="tools-menu"
              onClick={() => { setToolsOpen(true); setServicesOpen(false); }}
              onFocus={() => { setToolsOpen(true); setServicesOpen(false); }}
            >
              Tools
              <ChevronDown className={`h-4 w-4 transition-transform ${toolsOpen ? "rotate-180" : ""}`} aria-hidden="true" />
            </Link>
            <AnimatePresence>
              {toolsOpen && (
                <m.div
                  id="tools-menu"
                  initial={{ y: 8 }}
                  animate={{ y: 0 }}
                  exit={{ y: 8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-0 mt-2 w-96 rounded-2xl border border-white/10 bg-midnight-deep p-4 shadow-elevated"
                >
                  <p id="tools-menu-heading" className="mb-3 px-3 text-xs font-semibold uppercase tracking-wider text-frost/75">
                    Free tools and templates
                  </p>
                  <ul className="space-y-1" aria-labelledby="tools-menu-heading">
                    <li>
                      <Link
                        to="/tools"
                        className="block rounded-xl px-3 py-2 text-sm text-frost/90 transition hover:bg-white/5 hover:text-frost"
                      >
                        All tools and templates
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/scan"
                        className="block rounded-xl px-3 py-2 text-sm text-frost/90 transition hover:bg-white/5 hover:text-frost"
                      >
                        Free Operating Scan
                      </Link>
                    </li>
                    {tools.map((t) => (
                      <li key={t.path}>
                        <Link
                          to={t.path}
                          className="block rounded-xl px-3 py-2 text-sm text-frost/90 transition hover:bg-white/5 hover:text-frost"
                        >
                          {t.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </m.div>
              )}
            </AnimatePresence>
          </div>
          <NavLink to="/insights">Insights</NavLink>
          <NavLink to="/contact">Contact</NavLink>

          <Button
            asChild
            size="sm"
            className="ml-4 min-h-10 rounded-full bg-frost px-5 text-sm font-semibold text-midnight-deep hover:bg-white"
          >
            <Link to="/scan">Free Operating Scan</Link>
          </Button>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <Link
            to="/scan"
            className="inline-flex min-h-11 shrink-0 items-center whitespace-nowrap rounded-full bg-frost px-4 py-2.5 text-sm font-semibold text-midnight-deep"
          >
            <span className="sm:hidden">Free scan</span>
            <span className="hidden sm:inline">Free Operating Scan</span>
          </Link>
          <button
            ref={toggleRef}
            type="button"
            className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/10 text-frost"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu"
          >
            {mobileOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
          </button>
        </div>

      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <m.div
            id="mobile-menu"
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            className="max-h-[calc(100dvh-5rem)] overflow-y-auto overscroll-contain border-t border-white/10 bg-midnight-deep lg:hidden"
          >
            <nav aria-label="Mobile" className="mx-auto max-w-7xl space-y-1 px-6 py-6">
              <MobileLink to="/about">About</MobileLink>
              <MobileLink to="/how-we-work">How we work</MobileLink>
              <p id="mobile-services-heading" className="px-3 pt-3 text-xs font-semibold uppercase tracking-wider text-frost/75">
                Services
              </p>
              <ul aria-labelledby="mobile-services-heading" className="space-y-1">
                <li>
                  <MobileLink to="/services">All services</MobileLink>
                </li>
                {services.map((s) => (
                  <li key={s.to}>
                    <MobileLink to={s.to}>{s.name}</MobileLink>
                  </li>
                ))}
                {DECISION_SPRINT_LIVE ? (
                  <li>
                    <MobileLink to="/decision-sprint">Decision Sprint</MobileLink>
                  </li>
                ) : null}
              </ul>
              <MobileLink to="/specialisations">Expertise</MobileLink>
              <MobileLink to="/packages">Packages</MobileLink>

              <MobileLink to="/advisers">For Advisers and Partners</MobileLink>
              <p id="mobile-tools-heading" className="px-3 pt-3 text-xs font-semibold uppercase tracking-wider text-frost/75">
                Tools and Templates
              </p>
              <ul aria-labelledby="mobile-tools-heading" className="space-y-1">
                <li>
                  <MobileLink to="/tools">All tools and templates</MobileLink>
                </li>
                <li>
                  <MobileLink to="/scan">Free Operating Scan</MobileLink>
                </li>
                {tools.map((t) => (
                  <li key={t.path}>
                    <MobileLink to={t.path}>{t.name}</MobileLink>
                  </li>
                ))}
              </ul>
              <MobileLink to="/insights">Insights</MobileLink>
              <MobileLink to="/contact">Contact</MobileLink>
              <Link
                to="/scan"
                className="mt-4 block min-h-12 rounded-full bg-frost px-5 py-3.5 text-center text-sm font-semibold text-midnight-deep"
              >
                Free Operating Scan
              </Link>
            </nav>
          </m.div>
        )}
      </AnimatePresence>
    </header>
  );
}

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="rounded-full px-4 py-2 text-sm font-medium text-frost/90 transition hover:bg-white/5 hover:text-frost"
      activeProps={{ "aria-current": "page", className: "rounded-full px-4 py-2 text-sm font-medium text-frost bg-white/10" }}
    >
      {children}
    </Link>
  );
}

function MobileLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="block min-h-11 rounded-xl px-3 py-3 text-sm text-frost/90 transition hover:bg-white/5 hover:text-frost"
      activeProps={{ "aria-current": "page", className: "block min-h-11 rounded-xl px-3 py-3 text-sm text-frost bg-white/10" }}
    >
      {children}
    </Link>
  );
}

